import { html, render } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import { capitalize, cleanString } from '@polleverywhere/core/lib/util'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import './style.scss'

/**
 * Replaces a set of delimiter characters with whitespace.
 * @param {string} text Text input.
 * @return {string} Undelimited text.
 */
function undelimit (text) {
  return text.replace(/[_-]/g, ' ')
}

function textareaTemplate (attrs) {
  return html`<textarea class=${bem('textarea')}
    aria-label='${ifDefined(attrs.placeholder)}'
    autocapitalize=${ifDefined(attrs.autocapitalize)}
    autocorrect=${ifDefined(attrs.autocorrect)}
    cols='${ifDefined(attrs.cols)}'
    ?disabled=${attrs.disabled}
    maxlength='${ifDefined(attrs.maxlength)}'
    minlength='${ifDefined(attrs.minlength)}'
    name='${ifDefined(attrs.name)}'
    placeholder='${ifDefined(attrs.placeholder)}'
    ?required=${attrs.required}
    rows='${ifDefined(attrs.rows)}'
    spellcheck=${ifDefined(attrs.spellcheck)}
    @focus=${renderTemplate}
    @blur=${renderTemplate}
  >${attrs.value}</textarea>`
}

function inputTemplate (attrs) {
  return html`<input class=${bem('input')}
    aria-label='${ifDefined(attrs.placeholder)}'
    autocapitalize=${ifDefined(attrs.autocapitalize)}
    autocorrect=${ifDefined(attrs.autocorrect)}
    ?disabled=${attrs.disabled}
    max='${ifDefined(attrs.max)}'
    maxlength='${ifDefined(attrs.maxlength)}'
    min='${ifDefined(attrs.min)}'
    minlength='${ifDefined(attrs.minlength)}'
    name='${ifDefined(attrs.name)}'
    pattern='${ifDefined(attrs.pattern)}'
    placeholder='${ifDefined(attrs.placeholder)}'
    ?required=${attrs.required}
    spellcheck=${ifDefined(attrs.spellcheck)}
    type='${ifDefined(attrs.type)}'
    value='${ifDefined(attrs.value)}'
    @focus=${renderTemplate}
    @blur=${renderTemplate}
  />`
}

function renderTemplate () {
  if (!this.isConnected) return

  // Serializes all DOM attributes into an object for easy referencing.
  const boolAttrs = ['disabled', 'required', 'use-default-placeholder']
  const attrs = Object.fromEntries(this.getAttributeNames().map((name) => {
    let value = this.getAttribute(name) === '' && boolAttrs.includes(name)
      ? this.hasAttribute(name)
      : this.getAttribute(name) || undefined

    if (this.hasAttribute('sanitize') && name === 'value') {
      value = cleanString(value)
    }

    const kv = [
      name.toLowerCase(),
      value
    ]

    return kv
  }))

  const multiLine = attrs.type === 'textarea'

  render(html`<div>
    <div class=${bem('input-row')}>
      ${this.slots('before')}
      <label class=${bem('input-wrap', { multi: multiLine })}
        title='${ifDefined(attrs.placeholder)}'
        ?is-active=${this.isActive}>
        ${attrs.type === 'textarea'
          ? textareaTemplate(attrs)
          : inputTemplate(attrs)}
      </label>
      ${this.slots('after')}
    </div>
    <div class=${bem('error')}
      role='${ifDefined(attrs.error ? 'alert' : undefined)}'>
      ${attrs.error}
    </div>
  </div>`, this, { eventContext: this })
}

/**
 * Renders a text input field with validation checks based on native validity
 * states from the input field. This is essentially a wrapper around an
 * `<input>` or `<textarea>` element that exposes a consistent way for error
 * messages to be rendered back to the user.
 *
 * ```
 * // Single-line email text field
 * <text-field type='email' required></text-field>
 *
 * // Multi-line required text field
 * <text-field type='textarea' required></text-field>
 *
 * ```
 *
 * Custom error messages are supported and can simply be set as an attribute on
 * the element itself.
 * ```
 * <text-field error='Something went wrong.'></text-field>
 * ```
 *
 * Whenever the input field is invalid, an error message is included as an
 * attribute at the top-level. Consumers can check if the input field is valid
 * either by checking if the `error` attribute exists or calling the `valid()`
 * method.
 *
 * Consumers control how and when the text field undergoes validation.
 * Typically, you'll want to do this by simply listening for an `<input>` event
 * of your choice, i.e. `change`, `input`, `blur`, etc. and calling `validate()`
 * from within your event handler.
 *
 * ```
 * // Validate the text field whenever the input changes.
 * const tf = this.querySelector('text-field')
 * tf.addEventListener('input', (e) => tf.validate())
 * ```
 *
 * @element text-field
 *
 * @attr {string} autocapitalize - Set to 'off' to disable browser auto capitalization
 * @attr {string} autocorrect - Set to 'off' to disable browser auto correction
 * @attr {number} cols - For textarea types, number of columns.
 * @attr {boolean} disabled - Disables the text field from input.
 * @attr {string} error - Contains the error validation message whenever the
 *  input field is in an invalid state. Consumers can set this attribute to
 *  manually trigger their own validation error.
 * @attr {string} max - Max range limit.
 * @attr {number} maxlength - Max char limit.
 * @attr {string} min - Min range limit.
 * @attr {number} minlength - Min char limit.
 * @attr {string} name - Give the `<input>` element a name. This name will also
 *  show up in validation messages as the subject.
 * @attr {string} pattern - Regular expression pattern to match.
 * @attr {string} placeholder - Help text to display in the input field.
 * @attr {boolean} required - Make the input field required.
 * @attr {number} rows - For textarea types, number of rows.
 * @attr {string} spellcheck - Set to 'false' to disable browser spellcheck
 * @attr {string} type - Input type, i.e. email, url, password, etc
 * @attr {string} value - Default value to initialize input field to. Consumers
 *  should use the value property to update the input field afterwards.
 * @attr {boolean} sanitize - Sanitizes value for xss
 *
 * @prop {boolean} isActive - More for internal use, it evaluates whether the
 *  input field is in a somewhat "active" state, containing some input or having
 *  the document focus.
 * @prop {boolean} valid - Returns whether the text field is in a valid state.
 * @prop {string|number} value - Get/set the input element's value.
 *
 * @slot before - Element slot is inserted before (or to the left of) the input
 *  field. Consumers can use this to prefix the input with additional content.
 * @slot after - Element slot is inserted after (or to the right of) the input
 *  field. Consumers can use this to suffix the input with additional content.
 */
export default class TextField extends HTMLElement {
  static get tagName () { return 'text-field' }

  static get observedAttributes () {
    return [
      'autocapitalize',
      'autocorrect',
      'cols',
      'disabled',
      'error',
      'max',
      'maxlength',
      'min',
      'minlength',
      'name',
      'pattern',
      'placeholder',
      'required',
      'spellcheck',
      'rows',
      'type',
      'use-default-placeholder',
      'value',
      'sanitize'
    ]
  }

  /**
   * Evaluates whether the input field is in an "active" state, meaning it
   * either contains some input or has the document focus.
   * @return {boolean}
   */
  get isActive () {
    const input = this.querySelector('input') || this.querySelector('textarea')
    const hasFocus = document.hasFocus() && document.activeElement === input
    return input
      ? !!input.value.length || hasFocus
      : !!this.getAttribute('value')
  }

  /**
   * Returns whether the input is currently in a valid state or not.
   * @return {boolean}
   */
  get valid () {
    const input = this.querySelector('input') || this.querySelector('textarea')
    const disabledValue = input.disabled

    // Temporarily disable input before checking validity. Otherwise, disabled
    // inputs always return valid and ignores all constraint validations.
    input.disabled = false
    const valid = input.validity.valid && !this.hasAttribute('error')
    input.disabled = disabledValue

    return valid
  }

  get value () {
    const input = this.querySelector('input') || this.querySelector('textarea')
    const val = input ? input.value : this.getAttribute('value')

    if (this.hasAttribute('sanitize')) {
      return cleanString(val)
    }

    return val
  }

  set value (val) {
    const input = this.querySelector('input') || this.querySelector('textarea')
    input.value = val
    renderTemplate.call(this)
  }

  constructor () {
    super()
    defineElementSlot(this, 'before')
    defineElementSlot(this, 'after')
  }

  connectedCallback () {
    renderTemplate.call(this)
  }

  attributeChangedCallback () {
    renderTemplate.call(this)
  }

  /**
   * Validate the input field and toggle the error state.
   * @return {boolean} True if input field is valid. False otherwise.
   */
  validate () {
    const input = this.querySelector('input') || this.querySelector('textarea')
    const validity = input.validity
    const subject = capitalize(undelimit(input.name) || 'text field')

    // Override default validation messages with our own. All validity states
    // come with their own default messaging, which we can always revert back to
    // by removing the custom validity message for that property.
    if (validity.valueMissing) {
      input.setCustomValidity(`${subject} must have a value.`)
    } else if (validity.tooLong) {
      input.setCustomValidity(
        `${subject} must be ${input.maxLength} characters or less.`)
    } else if (validity.tooShort) {
      input.setCustomValidity(
        `${subject} must be ${input.minLength} characters or more.`)
    } else if (validity.typeMismatch) {
      const type = input.type === 'url' ? input.type.toUpperCase() : input.type
      input.setCustomValidity(`${subject} must be a valid ${type}.`)
    } else if (validity.patternMismatch) {
      input.setCustomValidity(`${subject} must match the requested format.`)
    } else {
      input.setCustomValidity('')
    }

    // Toggles error on/off depending on the input's validation message, which
    // will auto-populate whenever it is invalid.
    if (input.validationMessage) {
      this.setAttribute('error', input.validationMessage)
    } else {
      this.removeAttribute('error')
    }

    renderTemplate.call(this)
    return this.valid
  }
}

const bem = bemBlock(TextField.tagName)
register(module, TextField.tagName, TextField)
